// components/overlay/StatusRail.jsx
// The 3-slot live status rail — Now Drawing/Up Next · Now Cooking · Still
// Cooking/Just Finished. Extracted from Booth.jsx (2026-07-09) so the Main
// portrait info band and the Booth rail render the SAME cells: one design
// language, one set of text-fit rules.
//
// Text-fit rules (the whole reason this file exists — the old cells let long
// titles/timers escape the rail and slide under the live feed):
//   - every text row is a no-wrap flex row with overflow guards; nothing can
//     push a sibling out of the cell,
//   - titles line-clamp AND break long words (overflowWrap) instead of
//     clipping the last letters,
//   - portrait fonts clamp on vh so the cell degrades gracefully in ANY
//     viewport (preview windows, Lab thumbnails) instead of exploding when
//     the rail is shorter than the 1080×1920 rig canvas.
//
// Perf: one 1Hz interval scoped to StatusRow so the timers tick without
// re-rendering the host overlay.

import { useEffect, useState } from "react";
import { computeDisplayTitle } from "./../../utils/meditations";
import { fmtTimer, fmtClock, getRefSrc } from "./../../utils/overlayFormat";

const GOLD = "var(--jop-gold)";
const PINK = "var(--jop-pink)";
const D = "var(--jop-display)";

// Pan-flag column colors — bound to slot POSITION, not state (slot contents
// vary: slot 1 is Drawing or Up Next, slot 3 is Just Finished or an
// overlapping Still Cooking). Same vars as QueueCard's palette, so event
// themes recolor every scene together.
const SLOT1 = "var(--jop-slot-drawing)";
const SLOT2 = "var(--jop-slot-cooking)";
const SLOT3 = "var(--jop-slot-done)";
const SLOT1_RGB = "var(--jop-slot-drawing-rgb)";
const SLOT2_RGB = "var(--jop-slot-cooking-rgb)";
const SLOT3_RGB = "var(--jop-slot-done-rgb)";

const BAR_BG = "linear-gradient(90deg, var(--jop-grad-1) 0%, var(--jop-grad-2) 60%, var(--jop-grad-3) 100%)";

// Shell for the framed (Booth) rail — exported for GalaRail, which swaps the
// live cells for booking-pitch content inside the identical band.
// Portrait: a slim in-flow rail locked directly under the top bar — the
// "what's coming up" strip above the live feed it narrates. No borderTop:
// the TopBar's gold borderBottom already draws that line.
// Landscape: the classic full-height bar pinned to the screen bottom.
export const statusRowShell = (portrait) => ({
  ...(portrait
    ? {
        position: "relative", flexShrink: 0,
        height: "max(150px, 11vh)",
        borderBottom: `3px solid ${GOLD}`,
      }
    : {
        position: "absolute", left: 0, right: 0, bottom: 0,
        height: "max(134px, 17vh)",
        borderTop: `3px solid ${GOLD}`,
        boxShadow: "0 -3px 12px rgba(var(--jop-gold-rgb), 0.22)",
      }),
  background: BAR_BG,
  display: "flex", alignItems: "stretch",
  fontFamily: D,
  zIndex: 3,
});

export function Divider() {
  return (
    <div style={{
      width: "2px", alignSelf: "center", height: "70%",
      background: "rgba(255,255,255,0.15)",
      flexShrink: 0,
    }} />
  );
}

// Per-orientation type scale. `compact` = the Main info band's shorter rail
// (~160px of cell height vs Booth's ~210px): smaller thumb, single-line title.
// Portrait cells are ~170–190px of text width, so the queue # rides the thumb
// as a corner badge (qBadge) — the label row keeps its full width and "STILL
// COOKING" stays readable instead of ellipsizing into noise.
function cellSizes(portrait, compact) {
  if (!portrait) {
    return {
      thumb: "clamp(110px, 15vh, 230px)",
      label: "clamp(26px, 1.5vw, 30px)",
      qNum: "clamp(38px, 2.3vw, 46px)",
      qBadge: false,
      title: "clamp(34px, 2.2vw, 44px)",
      timer: "clamp(40px, 2.6vw, 50px)",
      caption: "clamp(20px, 1.1vw, 22px)",
      titleLines: 2,
    };
  }
  return compact
    ? {
        thumb: "clamp(76px, 6.4vh, 123px)",
        label: "clamp(11px, 0.95vh, 18px)",
        qNum: "clamp(13px, 1.35vh, 26px)",
        qBadge: true,
        title: "clamp(15px, 1.4vh, 27px)",
        timer: "clamp(18px, 1.85vh, 36px)",
        caption: "clamp(10px, 0.95vh, 18px)",
        titleLines: 1,
      }
    : {
        thumb: "clamp(90px, 7.4vh, 142px)",
        label: "clamp(12px, 1.05vh, 20px)",
        qNum: "clamp(14px, 1.5vh, 28px)",
        qBadge: true,
        title: "clamp(16px, 1.55vh, 30px)",
        timer: "clamp(20px, 2.1vh, 40px)",
        caption: "clamp(10px, 0.95vh, 18px)",
        titleLines: 2,
      };
}

// The stat (live timer / finished-at clock) is treated as a hero number, not
// a corner footnote: it's the thing visitors actually want off this board.
function StatusCell({ tone, toneRgb, label, order, statValue, statCaption, designMap, refPolicy = "full", portrait, compact }) {
  const title = order ? (computeDisplayTitle(order) || order.subjectName || "—") : null;
  const qNum = order?.queueNumber ?? null;
  // refPolicy "flashOnly" (Up Next slot): customer uploads are unvetted until
  // the artist actually starts drawing — a rejected/inappropriate upload must
  // never hit the public screen. Flash design art is ours, so it may render;
  // custom/magic/freestyle get a type glyph and identify by name + queue #.
  const isFlash = order?.orderType === "flash" || order?.orderType === "freestyle_flash";
  const allowRef = refPolicy === "full" || isFlash;
  const src = order ? (
    order.cutoutThumbnailUrl
    || order.photo_cutout_url
    || (allowRef && designMap ? getRefSrc(order, designMap) : null)
  ) : null;
  const glyph = order
    ? (order.isFreestyle ? "✏️" : order.orderType === "magic" ? "✨" : "🎨")
    : null;
  const fs = cellSizes(portrait, compact);

  return (
    <div style={{
      flex: 1, minWidth: 0, minHeight: 0,
      display: "flex", alignItems: "center",
      gap: portrait ? "0.8rem" : "clamp(0.8rem, 1.4vw, 1.8rem)",
      padding: portrait ? "0 0.7rem" : "0 clamp(0.9rem, 1.4vw, 1.8rem)",
      overflow: "hidden",
    }}>
      <div style={{
        width: fs.thumb, height: fs.thumb,
        maxHeight: "92%",
        flexShrink: 0,
        position: "relative",
        borderRadius: "14px",
        border: `3px solid ${tone}`,
        background: "rgba(0,0,0,0.3)",
        boxShadow: `inset 0 0 18px rgba(${toneRgb}, 0.2)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
        boxSizing: "border-box",
      }}>
        {src ? (
          <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        ) : (
          <div style={{ color: tone, opacity: refPolicy === "flashOnly" && glyph ? 0.85 : 0.5, fontSize: "2.6rem" }}>
            {refPolicy === "flashOnly" && glyph ? glyph : "—"}
          </div>
        )}
        {fs.qBadge && qNum != null && (
          <div style={{
            position: "absolute", top: "3px", left: "3px",
            background: "rgba(var(--jop-ink-rgb), 0.82)",
            borderRadius: "8px", padding: "1px 7px",
            color: "white", fontFamily: D, fontWeight: 900,
            fontSize: fs.qNum, lineHeight: 1.25,
          }}>#{qNum}</div>
        )}
      </div>
      <div style={{
        flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden",
        display: "flex", flexDirection: "column",
        justifyContent: "center", gap: "clamp(2px, 0.5vh, 8px)",
      }}>
        {/* Label (+ queue # in landscape) — one no-wrap row; the label
            ellipsizes before it can ever shove anything out of the cell. */}
        <div style={{
          display: "flex", alignItems: "baseline", gap: "0.6rem",
          minWidth: 0, overflow: "hidden",
        }}>
          <div style={{
            color: tone, fontFamily: D, fontWeight: 900,
            fontSize: fs.label, textTransform: "uppercase",
            letterSpacing: portrait ? "0.04em" : "0.06em", lineHeight: 1,
            minWidth: 0, overflow: "hidden",
            whiteSpace: "nowrap", textOverflow: "ellipsis",
          }}>{label}</div>
          {!fs.qBadge && qNum != null && (
            <div style={{
              color: "white", fontFamily: D, fontWeight: 900,
              fontSize: fs.qNum, lineHeight: 1, flexShrink: 0,
            }}>#{qNum}</div>
          )}
        </div>
        <div style={{
          color: "white", fontFamily: D, fontWeight: 700,
          fontSize: fs.title, lineHeight: 1.12,
          overflow: "hidden",
          // Single-line cells (Main's compact band) ellipsize mid-word —
          // "A Lighthou…" beats the line-clamp's "A…". Multi-line cells
          // clamp AND break long words ("Lighthouse") instead of clipping
          // their last letters at the cell edge.
          ...(fs.titleLines === 1
            ? { whiteSpace: "nowrap", textOverflow: "ellipsis" }
            : {
                display: "-webkit-box", WebkitLineClamp: fs.titleLines, WebkitBoxOrient: "vertical",
                overflowWrap: "anywhere",
              }),
        }}>
          {title || (order === null ? "—" : "loading…")}
        </div>
        {statValue && (
          // Portrait stacks the caption UNDER the timer — the narrow cell
          // can't fit "2:09 COOKING" on one line, and a clipped caption
          // reads broken. Landscape cells are wide; keep the inline baseline
          // pair there. Compact cells skip the caption entirely — the slot
          // label already names the activity.
          <div style={{
            display: "flex",
            ...(portrait
              ? { flexDirection: "column", gap: "1px", alignItems: "flex-start" }
              : { flexDirection: "row", alignItems: "baseline", gap: "0.5rem" }),
            minWidth: 0, overflow: "hidden", whiteSpace: "nowrap",
          }}>
            <div style={{
              color: tone, fontFamily: D, fontWeight: 900,
              fontSize: fs.timer, lineHeight: 1,
              fontVariantNumeric: "tabular-nums",
              flexShrink: 0,
            }}>{statValue}</div>
            {statCaption && !compact && (
              <div style={{
                color: "rgba(255,255,255,0.75)", fontFamily: D, fontWeight: 700,
                fontSize: fs.caption, textTransform: "uppercase",
                letterSpacing: "0.08em",
                minWidth: 0, maxWidth: "100%",
                overflow: "hidden", textOverflow: "ellipsis",
              }}>{statCaption}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Slot logic — POSITIONAL, so all 3 columns stay populated as often as
// possible (state-bound slots left holes during slow stretches / overlaps):
//   Slot 1: Now Drawing, else Up Next (lowest-# queued order — flash art may
//           preview; custom uploads are unvetted, name + glyph only).
//   Slot 2: newest pancake on the griddle (Now Cooking).
//   Slot 3: when 2+ are cooking, the OLDER griddle pancake (Still Cooking —
//           live overlap beats old news); else Just Finished with a static
//           "finished at" clock stamp (a finished pancake doesn't tick).
//
// `frameless` (Main portrait info band): no shell of its own — fills the
// parent row, renders `idleContent` when every slot is empty, and uses the
// compact cell scale. Default (Booth): the framed band shell + booth-voiced
// empty states.
export default function StatusRow({
  activeShift, drawing, cookingAll, queued, lastDone, designMap, portrait,
  frameless = false, idleContent = null,
}) {
  // 1Hz tick scoped to this component so the cook/draw timers update without
  // re-rendering the rest of the host overlay (top bars, spotlight, etc.).
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const queueOpen = !!activeShift?.isQueueOpen;

  if (!activeShift && !frameless) {
    return (
      <div style={statusRowShell(portrait)}>
        <div style={{
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
          color: GOLD, fontFamily: D, fontWeight: 700,
          fontSize: "clamp(40px, 2.8vw, 56px)",
          letterSpacing: "0.06em", textAlign: "center", padding: "0 1rem",
        }}>
          🎟 See us at the booth to start your pancake!
        </div>
      </div>
    );
  }

  // Oldest→newest by griddle arrival; a missing timestamp sorts oldest so it
  // never displaces a known-fresh pancake from slot 2.
  const cookStartMs = (o) => (o?.cookingStartedAt || o?.flippedAt)?.toMillis?.() ?? 0;
  const cookingSorted = [...(cookingAll || [])].sort((a, b) => cookStartMs(a) - cookStartMs(b));
  const newestCooking = cookingSorted[cookingSorted.length - 1] ?? null;
  const olderCooking = cookingSorted.length >= 2 ? cookingSorted[0] : null;

  // Up Next: suspended orders are excluded — suspension is how an operator
  // pulls a questionable upload, so it must never auto-promote to the screen.
  const upNext = drawing ? null : (queued || []).find((o) => o.state === "queued") ?? null;

  const drawTimer = drawing?.startedAt?.toMillis
    ? fmtTimer(now - drawing.startedAt.toMillis())
    : null;
  const cookTimerFor = (o) => {
    const ms = cookStartMs(o);
    return ms ? fmtTimer(now - ms) : null;
  };
  const doneAt = lastDone?.readyToDeliverAt;
  const finishedAt = doneAt?.toMillis ? fmtClock(doneAt.toMillis()) : null;
  // Portrait stacks the caption under the timer, so the full phrase fits.
  const cookCaption = "on the griddle";

  const allEmpty = !drawing && !upNext && !newestCooking && !lastDone;

  if (frameless) {
    if (allEmpty) return idleContent;
  } else if (!queueOpen && allEmpty) {
    return (
      <div style={statusRowShell(portrait)}>
        <div style={{
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
          color: PINK, fontFamily: D, fontWeight: 900,
          fontSize: "clamp(38px, 2.6vw, 52px)",
          letterSpacing: "0.06em",
        }}>
          QUEUE CLOSED — Thanks for visiting!
        </div>
      </div>
    );
  }

  const shell = frameless
    ? { flex: 1, minWidth: 0, minHeight: 0, display: "flex", alignItems: "stretch", fontFamily: D }
    : statusRowShell(portrait);
  const compact = frameless;

  return (
    <div style={shell}>
      {drawing ? (
        <StatusCell tone={SLOT1} toneRgb={SLOT1_RGB} label="Now Drawing" order={drawing} statValue={drawTimer} statCaption="drawing" designMap={designMap} portrait={portrait} compact={compact} />
      ) : (
        <StatusCell tone={SLOT1} toneRgb={SLOT1_RGB} label="Up Next" order={upNext} refPolicy="flashOnly" designMap={designMap} portrait={portrait} compact={compact} />
      )}
      <Divider />
      <StatusCell tone={SLOT2} toneRgb={SLOT2_RGB} label="Now Cooking" order={newestCooking} statValue={cookTimerFor(newestCooking)} statCaption={cookCaption} designMap={designMap} portrait={portrait} compact={compact} />
      <Divider />
      {olderCooking ? (
        <StatusCell tone={SLOT3} toneRgb={SLOT3_RGB} label="Still Cooking" order={olderCooking} statValue={cookTimerFor(olderCooking)} statCaption={cookCaption} designMap={designMap} portrait={portrait} compact={compact} />
      ) : (
        <StatusCell tone={SLOT3} toneRgb={SLOT3_RGB} label="Just Finished" order={lastDone || null} statValue={finishedAt} statCaption={null} designMap={designMap} portrait={portrait} compact={compact} />
      )}
    </div>
  );
}
